import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Snackbar,
  Alert,
  IconButton,
  Drawer,
  Tooltip,
  TextField,
  Button,
  CircularProgress,
  Skeleton,
  Chip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import Sidebar from '../components/Sidebar';
import { getOTPs } from '../api/services';

// ---------------------------------------------------------------
// Shared look (design tokens + small UI helpers) - same as Earnings
// ---------------------------------------------------------------
const ui = {
  primary: '#009db9',
  primaryDark: '#007a8a',
  primarySoft: '#e6f6f9',
  text: '#1f2933',
  muted: '#6b7280',
  border: '#e5e7eb',
  surface: '#f7f9fb',
  success: '#2e7d32',
  successSoft: '#e8f5e9',
  danger: '#d32f2f',
  dangerSoft: '#ffebee',
  warning: '#b26a00',
  warningSoft: '#fff4e5',
  radius: '16px',
  shadow: '0 4px 20px rgba(16, 24, 40, 0.06)',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#fff',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: ui.primary },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ui.primary },
    '&.Mui-disabled': { backgroundColor: '#f3f5f7' },
  },
  '& label.Mui-focused': { color: ui.primary },
};

const primaryButtonSx = {
  backgroundColor: ui.primary,
  color: '#fff',
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 700,
  px: 3,
  py: 1.25,
  boxShadow: 'none',
  '&:hover': { backgroundColor: ui.primaryDark, boxShadow: 'none' },
  '&.Mui-disabled': { backgroundColor: '#d5dbe0', color: '#8a949e' },
};

const headCellSx = {
  fontWeight: 700,
  color: ui.primaryDark,
  backgroundColor: ui.primarySoft,
  fontSize: '0.8rem',
  py: 1.5,
  borderBottom: `1px solid ${ui.border}`,
};

const bodyCellSx = {
  color: ui.text,
  py: 1.75,
  fontSize: '0.875rem',
  borderBottom: `1px solid ${ui.border}`,
};

const mobileCardSx = {
  border: `1px solid ${ui.border}`,
  borderRadius: '12px',
  p: 2,
  backgroundColor: '#fff',
};

const PageHeader = ({ title }) => (
  <Box sx={{ mb: { xs: 3, md: 4 } }}>
    <Typography
      variant="h4"
      sx={{
        fontWeight: 'bold',
        color: ui.text,
        letterSpacing: 0.5,
        fontSize: { xs: '1.5rem', sm: '1.85rem', md: '2.125rem' },
        position: 'relative',
        '&::after': {
          content: '""',
          display: 'block',
          backgroundColor: ui.primary,
          height: '4px',
          width: { xs: '80px', sm: '100px' },
          position: 'absolute',
          left: 0,
          bottom: '-8px',
        },
      }}
    >
      {title}
    </Typography>
  </Box>
);

const MenuButton = ({ onClick }) => (
  <Box
    sx={{
      display: { xs: 'block', md: 'none' },
      position: 'sticky',
      top: { xs: '64px', sm: '80px' },
      zIndex: 1100,
      mb: 2,
    }}
  >
    <Tooltip title="Menu" arrow>
      <IconButton
        onClick={onClick}
        sx={{
          background: 'linear-gradient(135deg, #009db9 0%, #007a8a 100%)',
          color: '#fff',
          borderRadius: '12px',
          p: 1.2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          '&:hover': {
            background: 'linear-gradient(135deg, #007a8a 0%, #005b66 100%)',
            boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
          },
          transition: 'all 0.3s ease',
        }}
        aria-label="toggle sidebar"
      >
        <MenuIcon sx={{ fontSize: '1.6rem' }} />
      </IconButton>
    </Tooltip>
  </Box>
);

const SectionCard = ({ title, action, children }) => (
  <Card
    sx={{
      mb: { xs: 2.5, md: 4 },
      borderRadius: ui.radius,
      border: `1px solid ${ui.border}`,
      backgroundColor: '#fff',
      boxShadow: ui.shadow,
      overflow: 'visible',
    }}
  >
    <CardContent
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        '&:last-child': { pb: { xs: 2, sm: 3, md: 4 } },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: ui.text,
            pl: 1.5,
            borderLeft: `4px solid ${ui.primary}`,
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </CardContent>
  </Card>
);

const chipTones = {
  success: { bg: ui.successSoft, color: ui.success },
  danger: { bg: ui.dangerSoft, color: ui.danger },
  warning: { bg: ui.warningSoft, color: ui.warning },
  info: { bg: ui.primarySoft, color: ui.primaryDark },
  neutral: { bg: '#eef1f4', color: '#4b5563' },
};

const PillChip = ({ label, tone = 'neutral' }) => {
  const t = chipTones[tone] || chipTones.neutral;
  return (
    <Chip
      size="small"
      label={label}
      sx={{ fontWeight: 700, borderRadius: '8px', backgroundColor: t.bg, color: t.color }}
    />
  );
};

const EmptyState = ({ icon: Icon = VpnKeyOutlinedIcon, title, message }) => (
  <Box sx={{ textAlign: 'center', py: { xs: 5, md: 7 }, px: 2 }}>
    <Box
      sx={{
        width: 64,
        height: 64,
        mx: 'auto',
        mb: 2,
        borderRadius: '50%',
        backgroundColor: ui.primarySoft,
        color: ui.primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon sx={{ fontSize: 32 }} />
    </Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ui.text }}>
      {title}
    </Typography>
    {message && (
      <Typography variant="body2" sx={{ color: ui.muted, mt: 0.5, maxWidth: 360, mx: 'auto' }}>
        {message}
      </Typography>
    )}
  </Box>
);

const ListSkeleton = ({ rows = 5 }) => (
  <Box role="status" aria-label="Loading" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton key={i} variant="rounded" height={56} animation="wave" />
    ))}
  </Box>
);

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AdminOTPManagement = () => {
  const [email, setEmail] = useState('');
  const [otps, setOtps] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [mobileOpen, setMobileOpen] = useState(false);

  const fetchOTPs = async () => {
    setLoading(true);
    setError('');
    setOtps([]);

    const emailError = validateEmail(email);
    if (emailError) {
      setError(emailError);
      setLoading(false);
      return;
    }

    try {
      const response = await getOTPs(email, page, rowsPerPage);
      if (!response || (!response.currentOTP && response.length === 0)) {
        setError('No OTPs found.');
        setSnackbarMessage('No OTPs found for the specified email.');
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
      } else {
        // Handle single OTP object or array
        const otpData = Array.isArray(response) ? response : [response];
        const mappedOtps = otpData.map((otp, index) => ({
          id: otp.id || `otp-${index}`, // Fallback ID if not provided
          currentOTP: otp.currentOTP,
          associatedEmail: otp.email || email,
          otpExpirationTime: new Date(otp.otpExpirationTime).toLocaleString(),
          otpUsed: otp.otpUsed ? 'Yes' : 'No',
          createdAt: new Date(otp.createdAt || otp.otpExpirationTime).toLocaleString(),
        }));
        // Sort OTPs by expiration time (most recent first)
        const sortedOtps = mappedOtps.sort((a, b) => 
          new Date(b.otpExpirationTime) - new Date(a.otpExpirationTime)
        );
        setOtps(sortedOtps);
        setSnackbarMessage('OTPs fetched successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (err) {
      const errorMessage =
        err.response?.status === 403
          ? 'Access denied. You do not have permission to view OTPs.'
          : err.response?.data?.message === 'user not found'
          ? 'No OTPs found for the specified email.'
          : err.response?.data?.message || 'Failed to fetch OTPs. Please try again.';
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setOtps([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value.trim();
    setEmail(newEmail);
    setError(validateEmail(newEmail));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchOTPs();
  };

  const validateEmail = (email) => {
    if (!email) return 'Email is required';
    if (!EMAIL_REGEX.test(email)) return 'Please enter a valid email address';
    return null;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchOTPs();
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    fetchOTPs();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Presentation-only helper
  const usedChip = (used) => (
    <PillChip label={used === 'Yes' ? 'Used' : 'Not used'} tone={used === 'Yes' ? 'neutral' : 'info'} />
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Box
        sx={{
          maxWidth: '1200px',
          mx: 'auto',
          width: '100%',
          flexGrow: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Sidebar />
        </Box>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            p: { xs: 2, md: 4 },
            pt: { xs: '50px', sm: '60px', md: '120px' },
            backgroundColor: '#fff',
            minHeight: '100vh',
            ml: { md: '20px' },
            pr: { xs: 1, md: 3 },
            position: 'relative',
          }}
        >
          <MenuButton onClick={handleDrawerToggle} />

          <PageHeader title="OTP Management" />

          {/* ------------------------------------------------------ */}
          {/* OTP lookup form                                        */}
          {/* ------------------------------------------------------ */}
          <SectionCard title="OTP lookup">
            <Box component="form" onSubmit={handleSubmit}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'flex-start' },
                  gap: 2,
                }}
              >
                <TextField
                  fullWidth
                  id="email-input"
                  label="Enter User Email"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  required
                  disabled={loading}
                  variant="outlined"
                  sx={fieldSx}
                  helperText={error || 'Enter the email of the user to fetch their OTP(s)'}
                  error={!!error}
                  aria-label="User email for OTP lookup"
                />
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ ...primaryButtonSx, minWidth: { sm: 170 }, height: 56, flexShrink: 0 }}
                  disabled={loading || !!error}
                  aria-label="Fetch OTPs"
                  startIcon={loading ? null : <SearchIcon />}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Fetch OTPs'}
                </Button>
              </Box>
            </Box>
          </SectionCard>

          {/* ------------------------------------------------------ */}
          {/* Results                                                */}
          {/* ------------------------------------------------------ */}
          {loading ? (
            <SectionCard title="OTP details">
              <ListSkeleton rows={4} />
            </SectionCard>
          ) : otps.length > 0 ? (
            <SectionCard title="OTP details">
              {/* Desktop table */}
              <Box
                sx={{
                  display: { xs: 'none', lg: 'block' },
                  overflowX: 'auto',
                  border: `1px solid ${ui.border}`,
                  borderRadius: '12px',
                }}
              >
                <Table aria-label="OTP table" sx={{ minWidth: 800 }}>
                  <TableHead>
                    <TableRow>
                      {['ID', 'OTP', 'Associated email', 'Expiration time', 'Used', 'Created at'].map(
                        (h) => (
                          <TableCell key={h} sx={headCellSx}>
                            {h}
                          </TableCell>
                        )
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {otps.map((otp) => (
                      <TableRow
                        key={otp.id}
                        hover
                        sx={{ '&:last-child td': { borderBottom: 0 } }}
                      >
                        <TableCell sx={{ ...bodyCellSx, wordBreak: 'break-word' }}>{otp.id}</TableCell>
                        <TableCell
                          sx={{ ...bodyCellSx, fontWeight: 700, letterSpacing: 1.5, color: ui.primaryDark }}
                        >
                          {otp.currentOTP}
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, wordBreak: 'break-word' }}>
                          {otp.associatedEmail}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>{otp.otpExpirationTime}</TableCell>
                        <TableCell sx={bodyCellSx}>{usedChip(otp.otpUsed)}</TableCell>
                        <TableCell sx={bodyCellSx}>{otp.createdAt}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              {/* Mobile / tablet cards */}
              <Box
                sx={{
                  display: { xs: 'flex', lg: 'none' },
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                {otps.map((otp) => (
                  <Box key={otp.id} sx={mobileCardSx}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 1,
                        mb: 1.5,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{ fontWeight: 700, color: ui.primaryDark, letterSpacing: 1.5, fontSize: '1.1rem' }}
                        >
                          {otp.currentOTP}
                        </Typography>
                        <Typography variant="body2" sx={{ color: ui.muted, wordBreak: 'break-word' }}>
                          {otp.associatedEmail}
                        </Typography>
                      </Box>
                      {usedChip(otp.otpUsed)}
                    </Box>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: 'minmax(0, 1fr)',
                          sm: 'repeat(2, minmax(0, 1fr))',
                        },
                        gap: 1,
                        p: 1.5,
                        borderRadius: '10px',
                        backgroundColor: ui.surface,
                      }}
                    >
                      <Box>
                        <Typography variant="caption" sx={{ color: ui.muted }}>
                          Expiration time
                        </Typography>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                          {otp.otpExpirationTime}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: ui.muted }}>
                          Created at
                        </Typography>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                          {otp.createdAt}
                        </Typography>
                      </Box>
                      <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                        <Typography variant="caption" sx={{ color: ui.muted }}>
                          ID
                        </Typography>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', wordBreak: 'break-word' }}>
                          {otp.id}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={otps.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  mt: 2,
                  color: ui.text,
                  '& .MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    justifyContent: { xs: 'center', sm: 'flex-end' },
                    px: 0,
                  },
                  '& .MuiTablePagination-spacer': { display: { xs: 'none', sm: 'block' } },
                }}
              />
            </SectionCard>
          ) : email ? (
            <SectionCard title="OTP details">
              <EmptyState
                title="No OTPs found"
                message="No OTPs found for this email."
              />
            </SectionCard>
          ) : (
            <SectionCard title="OTP details">
              <EmptyState
                title="Look up a user's OTPs"
                message="Enter an email to fetch OTPs."
              />
            </SectionCard>
          )}
        </Box>
      </Box>

      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          zIndex: 1100,
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 250,
            top: { xs: '64px', sm: '80px' },
            height: 'calc(100% - 64px)',
            borderRight: 'none',
          },
        }}
      >
        <Sidebar onItemClick={handleDrawerToggle} />
      </Drawer>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%', borderRadius: '10px' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminOTPManagement;